import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Sparkles, Loader2, Trash2, Crown, BookOpen, ListChecks, Target, Calendar, LineChart } from "lucide-react";

/**
 * CoachDrawer — persistent AI tutor slide-out (Gemini 2.5 Flash, Pro-only,
 * capped at 30 questions/day).
 * Props:
 *   open (bool): drawer visibility
 *   onClose (fn)
 *   topicId (string|null): scopes the conversation to a topic
 *   topicName (string): shown in header
 */
export default function CoachDrawer({ open, onClose, topicId, topicName, prefill }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [locked, setLocked] = useState(null); // { message } when 402 pro_only
  const [dailyRemaining, setDailyRemaining] = useState(null); // null = unknown, admin = null
  const [dailyLimit, setDailyLimit] = useState(20);
  const scrollerRef = useRef(null);

  // Load existing session + daily-usage snapshot when opened
  useEffect(() => {
    if (!open) return;
    setSessionLoaded(false);
    setLocked(null);
    api
      .get("/coach/session", { params: { topic_id: topicId || undefined } })
      .then((r) => setMessages(r.data.messages || []))
      .catch((e) => {
        setMessages([]);
        const d = e?.response?.data?.detail;
        if (e?.response?.status === 402 && d?.code === "pro_only") {
          setLocked({ message: d.message });
        }
      })
      .finally(() => setSessionLoaded(true));
    api
      .get("/coach/usage")
      .then((r) => {
        setDailyLimit(r.data.limit || 20);
        setDailyRemaining(r.data.admin_bypass ? null : r.data.remaining);
      })
      .catch(() => {});
  }, [open, topicId]);

  // Pre-fill the input when a caller (e.g., Ask-the-Coach button) passes prefill
  useEffect(() => {
    if (open && prefill) {
      setInput(prefill);
    }
  }, [open, prefill]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, ts: new Date().toISOString() }]);
    setLoading(true);
    try {
      const r = await api.post("/coach/message", {
        topic_id: topicId || null,
        message: msg,
      });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply, ts: new Date().toISOString() }]);
      if (r.data.daily_remaining !== undefined && r.data.daily_remaining !== null) {
        setDailyRemaining(r.data.daily_remaining);
      }
      window.dispatchEvent(new Event("pm-billing-refresh"));
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402 && detail?.code === "pro_only") {
        setLocked({ message: detail.message });
        setMessages((m) => m.slice(0, -1));
        setInput(msg);
      } else if (e.response?.status === 429 && detail?.code === "coach_daily_limit") {
        // 20/day tutor cap — friendly full-stop message, don't re-queue input.
        setDailyRemaining(0);
        toast.error(detail.message || "You've reached your daily tutor limit. See you tomorrow!");
        setMessages((m) => m.slice(0, -1));
        setInput(msg);
      } else if (e.response?.status === 402 && detail?.error === "out_of_quota") {
        toast.error(detail.reason || "Out of credits.", {
          action: { label: "Upgrade", onClick: () => (window.location.href = "/app/plans") },
        });
        setMessages((m) => m.slice(0, -1));
        setInput(msg);
      } else {
        toast.error(typeof detail === "string" ? detail : "Coach failed. Try again.");
        setMessages((m) => m.slice(0, -1));
        setInput(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear this coach conversation?")) return;
    try {
      await api.delete("/coach/session", { params: { topic_id: topicId || undefined } });
      setMessages([]);
      toast.success("Coach thread cleared.");
    } catch (e) {
      toast.error("Failed to clear.");
    }
  };

  const QUICK_PROMPTS = topicName
    ? [
        { icon: BookOpen, label: `Explain ${topicName} simply`, text: `Explain ${topicName} in simple words, using an analogy.` },
        { icon: Target, label: "Why is this answer correct?", text: "Explain why the correct answer to the last question is correct." },
        { icon: ListChecks, label: "Give me practice questions", text: `Give me 5 practice questions about ${topicName}.` },
        { icon: Sparkles, label: "Give me a fresh analogy", text: `Give me a fresh, memorable analogy for ${topicName}.` },
      ]
    : [
        { icon: BookOpen, label: "Explain a topic", text: "Explain photosynthesis in simple words with an analogy." },
        { icon: Target, label: "Help me solve a question", text: "Help me solve a physics problem step by step: A ball is thrown up at 10 m/s. How high does it go?" },
        { icon: ListChecks, label: "Create a quiz", text: "Create a 10-question medium-difficulty quiz about biology cell division." },
        { icon: Calendar, label: "Prepare for an exam", text: "Help me build a 7-day study plan for the MCAT biology section." },
        { icon: LineChart, label: "Find my weak topics", text: "Look at my Question Bank history and tell me what topics I should review." },
        { icon: Sparkles, label: "Give me practice questions", text: "Give me 5 medium-difficulty practice questions about biochemistry." },
      ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-black/30 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        data-testid="coach-backdrop"
      />
      {/* Drawer */}
      <aside
        className={`fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[460px] flex flex-col shadow-2xl transition-transform ${open ? "translate-x-0" : "translate-x-full"}`}
        style={{ background: "var(--paper)", borderLeft: "1px solid var(--border)" }}
        data-testid="coach-drawer"
        aria-hidden={!open}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#2A5CE0,#7B4DFF)" }}>
              <Sparkles size={16} color="white" />
            </div>
            <div>
              <div className="font-display font-bold text-sm flex items-center gap-1.5" data-testid="coach-title">
                AI Study Coach
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ background: "#c98a3b22", color: "#c98a3b" }}>PRO</span>
              </div>
              {topicName && <div className="text-xs" style={{ color: "var(--text-muted)" }}>on {topicName}</div>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && !locked && (
              <button
                onClick={clear}
                className="p-2 rounded-lg hover:bg-[var(--muted)]"
                title="Clear conversation"
                data-testid="coach-clear-btn"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-[var(--muted)]"
              data-testid="coach-close-btn"
              aria-label="Close coach"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="coach-messages">
          {locked ? (
            <div className="pm-card p-5 text-center" data-testid="coach-pro-lock">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ background: "#c98a3b22" }}>
                <Crown size={22} style={{ color: "#c98a3b" }} />
              </div>
              <div className="font-display font-bold mb-1">Pro-only feature</div>
              <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
                {locked.message}
              </p>
              <Link
                to="/app/plans"
                onClick={onClose}
                className="pm-btn-primary inline-flex items-center gap-1.5 text-sm"
                data-testid="coach-upgrade-btn"
              >
                <Crown size={14} /> Upgrade to Pro
              </Link>
            </div>
          ) : sessionLoaded && messages.length === 0 ? (
            <div className="pm-card p-4" data-testid="coach-empty">
              <div className="font-display font-bold text-base mb-1">Meet Your AI Study Coach</div>
              <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
                Your personal AI tutor is here to help you understand concepts, practice questions, prepare for exams, and improve the topics you find difficult.
              </p>
              <div className="grid grid-cols-1 gap-1.5" data-testid="coach-suggestions">
                {QUICK_PROMPTS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <button
                      key={p.label}
                      onClick={() => setInput(p.text)}
                      className="text-xs px-3 py-2 rounded-lg text-left inline-flex items-center gap-2 hover:bg-[var(--muted)] border"
                      style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}
                    >
                      <Icon size={12} style={{ color: "var(--primary)" }} />
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
          {messages.map((m, i) => (
            <div
              key={i}
              className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "ml-auto" : ""}`}
              style={{
                background: m.role === "user" ? "var(--primary)" : "var(--muted)",
                color: m.role === "user" ? "white" : "var(--text)",
                whiteSpace: "pre-wrap",
              }}
              data-testid={`coach-msg-${m.role}-${i}`}
            >
              {m.content}
            </div>
          ))}
          {loading && (
            <div
              className="max-w-[85%] p-3 rounded-2xl text-sm inline-flex items-center gap-2"
              style={{ background: "var(--muted)", color: "var(--text-muted)" }}
              data-testid="coach-thinking"
            >
              <Loader2 size={14} className="animate-spin" /> Thinking…
            </div>
          )}
        </div>

        {/* Input */}
        {!locked && (
          <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
            {dailyRemaining !== null && dailyRemaining <= 0 && (
              <div
                className="text-xs mb-2 p-2 rounded-lg text-center"
                style={{ background: "#c98a3b22", color: "#c98a3b" }}
                data-testid="coach-daily-limit-banner"
              >
                You've reached your daily tutor limit. See you tomorrow!
              </div>
            )}
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={dailyRemaining !== null && dailyRemaining <= 0 ? "Daily limit reached — back tomorrow!" : "Ask the coach…"}
                rows={2}
                maxLength={2000}
                className="flex-1 resize-none p-2 rounded-xl text-sm border"
                style={{ borderColor: "var(--border)", background: "var(--bg)" }}
                disabled={loading || (dailyRemaining !== null && dailyRemaining <= 0)}
                data-testid="coach-input"
              />
              <button
                onClick={send}
                disabled={loading || !input.trim() || (dailyRemaining !== null && dailyRemaining <= 0)}
                className="p-2.5 rounded-xl disabled:opacity-40"
                style={{ background: "var(--primary)", color: "white" }}
                data-testid="coach-send-btn"
                aria-label="Send"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </button>
            </div>
            <div className="text-[10px] mt-1.5 text-center" style={{ color: "var(--text-muted)" }} data-testid="coach-usage-hint">
              Powered by Gemini 2.5 Flash · 0.25 credit / message
              {dailyRemaining !== null && (
                <> · <b>{dailyRemaining}</b> of {dailyLimit} questions left today</>
              )}
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
